import { EventToNotification } from './eventToNotification';
import EstimationsDraftSaveSucceed from '../../estimations/events/EstimationsDraftSaveSucceed';
import Notification, { NotificationType } from '../Notification';
import * as uniqid from 'uniqid';
import SaveOrderDraftSucceed from '../../order/events/OrderSaveDraftSucceed';
import DataExportFailed from '../../products/events/DataExportFailed';
import OrderSetBaseOnLiabilityFailed from '../../order/events/OrderSetBaseOnLiabilityFailed';
import ExcelImportWrongFileLoaded from '../events/ExcelImportWrongFileLoaded';
import OrderImportFailed from '../../order/events/OrderImportFailed';
import OrderImportSucceed from '../../order/events/OrderImportSucceed';
import EstimationsImportSucceed from '../../estimations/events/EstimationsImportSucceed';
import EstimationsImportFailed from '../../estimations/events/EstimationsImportFailed';
import EstimationsPlaceFailed from '../../estimations/events/EstimationsPlaceFailed';
import EstimationsPlaceSucceed from '../../estimations/events/EstimationsPlaceSucceed';
import OrderPlaceSucceed from '../../order/events/OrderPlaceSucceed';
import TermsRequested from '../../estimations/events/TermsRequested';
import OrderPlaceFailed from '../../order/events/OrderPlaceFailed';
import EstimationSafeDraftFailed from '../../estimations/events/EstimationSafeDraftFailed';
import OrderSafeDraftFailed from '../../order/events/OrderSafeDraftFailed';
import OrderValidated from '../../order/events/OrderValidated';
import translation from '../../../domain/orders/validators/translation';
import OrderDocumentGetFailed from '../../order/events/OrderDocumentGetFailed';
import RequestFailed from '../events/RequestFailed';
import RemoveCustomerRequested from '../../estimations/events/RemoveCustomerRequested';
import ResetPasswordRequested from '../../estimations/events/ResetPasswordRequested';
import ResetPasswordDone from '../../estimations/events/ResetPasswordDone';

const eventToNotificationConfig: EventToNotification[] = [
    {
        type: OrderSetBaseOnLiabilityFailed.type,
        notification: 'Operation failed'
    },
    {
        type: DataExportFailed.type,
        notification: 'Export failed'
    },
    {
        type: EstimationsDraftSaveSucceed.type,
        notificationType: NotificationType.Info,
        notification: 'Saving estimations draft was successful'
    },
    {
        type: EstimationsPlaceSucceed.type,
        notification: (e: EstimationsPlaceSucceed): Notification[] => [
            new Notification(uniqid(), {
                notificationType: NotificationType.Info,
                event: e,
                hasButton: true
            })
        ]
    },
    {
        type: SaveOrderDraftSucceed.type,
        notification: 'Saving order draft was successful',
        notificationType: NotificationType.Info
    },
    {
        type: OrderPlaceSucceed.type,
        notification: (e: OrderPlaceSucceed): Notification[] => [
            new Notification(uniqid(), {
                notificationType: NotificationType.Info,
                event: e,
                hasButton: true
            })
        ]
    },
    {
        type: TermsRequested.type,
        notification: (e: TermsRequested): Notification[] => [
            new Notification(uniqid(), {
                message: '',
                notificationType: NotificationType.Info,
                event: e,
                hasButton: true
            })
        ]
    },
    {
        type: RemoveCustomerRequested.type,
        notification: (e: RemoveCustomerRequested): Notification[] => [
            new Notification(uniqid(), {
                message: '',
                notificationType: NotificationType.Info,
                event: e,
                hasButton: true
            })
        ]
    },
    {
        type: ResetPasswordRequested.type,
        notification: (e: ResetPasswordRequested): Notification[] => [
            new Notification(uniqid(), {
                message: '',
                notificationType: NotificationType.Info,
                event: e,
                hasButton: true
            })
        ]
    },
    {
        type: ResetPasswordDone.type,
        notification: (e: ResetPasswordDone): Notification[] => [
            new Notification(uniqid(), {
                message: 'New password: ' + e.password,
                notificationType: NotificationType.Info,
                hasButton: true
            })
        ]
    },
    {
        type: EstimationsImportFailed.type,
        notification: (e: EstimationsImportFailed): Notification[] =>
            e.errors.map((error: string) => {
                return new Notification(uniqid(), {
                    message: error,
                    hasButton: true
                });
            })
    },
    {
        type: OrderImportFailed.type,
        notification: (e: OrderImportFailed): Notification[] => {
            const config = {
                noItemsToOrder: 'No items to order'
            };
            return e.errors.map((error: string) => {
                return new Notification(uniqid(), {
                    message: config[error] || error,
                    hasButton: true
                });
            });
        }
    },
    {
        type: OrderImportSucceed.type,
        notification: (e: OrderImportSucceed): Notification[] => [
            new Notification(uniqid(), {
                message: '',
                notificationType: NotificationType.Info,
                event: e,
                hasButton: true
            })
        ]
    },
    {
        type: EstimationsImportSucceed.type,
        notification: (e: EstimationsImportSucceed): Notification[] =>
            e.errors
                .map((error: string) => {
                    return new Notification(uniqid(), {
                        message: error,
                        hasButton: true
                    });
                })
                .concat(
                    e.rowsToImport.length > 0
                        ? new Notification(uniqid(), {
                              message: 'Your file has been imported.',
                              notificationType: NotificationType.Info,
                              event: undefined
                          })
                        : []
                )
    },
    {
        type: ExcelImportWrongFileLoaded.type,
        notification: (e: ExcelImportWrongFileLoaded): Notification[] => [
            new Notification(uniqid(), {
                message: 'Incorrect file type.',
                hasButton: true
            })
        ]
    },
    {
        type: EstimationsPlaceFailed.type,
        notification: (e: EstimationsPlaceFailed): Notification[] => [
            new Notification(uniqid(), {
                message: 'Placing estimation has failed. Please rewise estimation quantities and try to place the estimation again.',
                hasButton: true
            })
        ]
    },
    {
        type: OrderPlaceFailed.type,
        notification: (e: OrderPlaceFailed): Notification[] =>
            e.status === 'monthNotValid'
                ? [
                      new Notification(uniqid(), {
                          message: 'Month in order panel does not match current calendar month. Please refresh.',
                          hasButton: true
                      })
                  ]
                : [
                      new Notification(uniqid(), {
                          message: 'No chosen items available, please refresh the page.',
                          hasButton: true
                      })
                  ]
    },
    {
        type: EstimationSafeDraftFailed.type,
        notification: (e: EstimationSafeDraftFailed): Notification[] => [
            new Notification(uniqid(), {
                message:
                    'Saving estimation draft has failed. Please rewise estimation quantities and try to save the estimation draft again.',
                hasButton: true
            })
        ]
    },
    {
        type: OrderSafeDraftFailed.type,
        notification: (e: OrderSafeDraftFailed): Notification[] => [
            new Notification(uniqid(), {
                message: 'Saving order draft has failed. Please rewise order quantities and try to save the order draft again.',
                hasButton: true
            })
        ]
    },
    {
        type: OrderValidated.type,
        notification: (e: OrderValidated): Notification[] => [
            new Notification(uniqid(), {
                message: translation[e.error]
            })
        ]
    },
    {
        type: OrderDocumentGetFailed.type,
        notification: (e: OrderDocumentGetFailed): Notification[] => [
            new Notification(uniqid(), {
                message: 'Document failed to load: ' + e.documentType + ', ' + e.documentId
            })
        ]
    },
    {
        type: RequestFailed.type,
        notification: (e: RequestFailed): Notification[] =>
            e.status === 401
                ? [
                      new Notification(uniqid(), {
                          message: 'Session expired'
                      })
                  ]
                : []
    }
];

export default eventToNotificationConfig;



// WEBPACK FOOTER //
// ./src/application/core/middleware/eventToNotificationConfig.ts